/**
 * MEMBER SEARCH (LOGIN-TO-TENANT.md §8.1) — the ONE way to pick workspace members, at any workspace size.
 *
 * The established pattern (WAI-ARIA 1.2 combobox; GitHub's assignee picker, Linear, Slack's people search):
 *   • the server searches — type ≥ 2 characters of a NAME (any word: "ama", "pani") or an EMAIL → the top 10 matches
 *     (debounced, stale replies dropped, cached per query); never "load every member and filter in the browser";
 *   • suggestions on focus before typing (callers pass e.g. the room roster or recent picks);
 *   • ↑/↓ to move, Enter to pick, Esc to close, Backspace on an empty box removes the last chip (multi);
 *   • picked people show as chips with their name — a saved email is resolved with ONE exact lookup;
 *   • the list renders through PortalMenu (never clipped by an overflow ancestor).
 *
 * Single: ``value`` is an email string, ``onChange(email)`` ('' = cleared).
 * Multi:  ``value`` is an array of emails, ``onChange(emails[])``.
 */
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { PortalMenu } from '../PortalMenu.jsx';
import { getRestApiBearerToken } from '../../services/apiToken.js';
import { listWorkspaceMembers } from '../../services/workspaceApi.js';
import { isPlaceholderEmail } from '../../utils/placeholderEmail.js';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';
export const MIN_CHARS = 2;
export const RESULT_LIMIT = 10;
export const DEBOUNCE_MS = 200;

const norm = (s) => (s || '').trim().toLowerCase();

/** Bold the part that matched: the start of the text, or the start of any word in it (names: "Amaresh PANI"). */
function Highlight({ text, prefix }) {
  const t = text || '';
  const p = norm(prefix).split(/\s+/)[0] || '';
  if (!p) return <>{t}</>;
  const lower = t.toLowerCase();
  let at = lower.startsWith(p) ? 0 : -1;
  if (at < 0) {
    const m = new RegExp(`[^a-z0-9]${p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`).exec(lower);
    at = m ? m.index + 1 : -1;
  }
  if (at < 0) return <>{t}</>;
  return (
    <>
      {t.slice(0, at)}
      <span className="font-semibold text-gray-900">{t.slice(at, at + p.length)}</span>
      {t.slice(at + p.length)}
    </>
  );
}

export default function MemberSearch({
  tenantId,
  mode = 'single',
  value,
  onChange,
  placeholder = 'Type a name or email…',
  excludeEmails = [],
  suggestions = [],
  disabled = false,
  ariaLabel = 'Workspace member',
  emptyHint = '',
  apiBase = API_BASE_URL,
  testId = 'member-search',
  size = 'md',             // 'sm' = 28 px tall, rounded-md — the height of a compact (text-xs py-1.5) button beside it
}) {
  const multi = mode === 'multi';
  // a placeholder (supervisor@example.com from older generated networks) is "no one" — never shown as a pick
  const selected = useMemo(() => {
    if (multi) return (Array.isArray(value) ? value : []).map(norm).filter((e) => e && !isPlaceholderEmail(e));
    const v = norm(value);
    return v && !isPlaceholderEmail(v) ? [v] : [];
  }, [multi, value]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [names, setNames] = useState({});            // email → display name (from searches / exact lookups)
  const anchorRef = useRef(null);
  const inputRef = useRef(null);
  const cache = useRef(new Map());
  const seq = useRef(0);
  const listId = useId();

  const search = useCallback(async (q) => {
    const tid = (tenantId || '').trim();
    if (!tid) return [];
    const key = `${tid}|${q}`;
    if (cache.current.has(key)) return cache.current.get(key);
    const data = await listWorkspaceMembers(apiBase, getRestApiBearerToken(), tid, { q, limit: RESULT_LIMIT });
    const rows = (data.members || []).filter((m) => (m.status || 'ACTIVE').toUpperCase() !== 'REVOKED' && m.email);
    cache.current.set(key, rows);
    return rows;
  }, [tenantId, apiBase]);

  // debounced server search; a reply for an older query never overwrites a newer one
  useEffect(() => {
    const q = norm(query);
    if (q.length < MIN_CHARS) {
      setResults([]);
      setLoading(false);
      setError('');
      return undefined;
    }
    const my = ++seq.current;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const rows = await search(q);
        if (my !== seq.current) return;
        setResults(rows);
        setNames((n) => ({ ...n, ...Object.fromEntries(rows.map((m) => [norm(m.email), m.displayName || ''])) }));
        setError('');
      } catch (e) {
        if (my === seq.current) setError((e && e.message) || 'Search failed');
      } finally {
        if (my === seq.current) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query, search]);

  // a saved email shows its name: ONE exact lookup each (no full member load)
  useEffect(() => {
    const unknown = selected.filter((e) => !(e in names));
    if (!unknown.length || !(tenantId || '').trim()) return undefined;
    let gone = false;
    Promise.all(unknown.map((e) => search(e).then((rows) => [e, (rows.find((m) => norm(m.email) === e) || {}).displayName || ''])
      .catch(() => [e, ''])))
      .then((pairs) => { if (!gone) setNames((n) => ({ ...n, ...Object.fromEntries(pairs) })); });
    return () => { gone = true; };
  }, [selected, names, search, tenantId]);

  const hidden = useMemo(() => new Set([...selected, ...(excludeEmails || []).map(norm)]), [selected, excludeEmails]);
  const typed = norm(query).length >= MIN_CHARS;
  const options = useMemo(() => {
    const src = typed ? results : (suggestions || []).map((s) => ({ email: s.email, displayName: s.name || s.displayName }));
    return src.filter((m) => m && m.email && !hidden.has(norm(m.email))).slice(0, RESULT_LIMIT);
  }, [typed, results, suggestions, hidden]);

  useEffect(() => { setActive(0); }, [options.length, query]);

  const pick = (m) => {
    const em = norm(m.email);
    if (!em) return;
    setNames((n) => ({ ...n, [em]: m.displayName || n[em] || '' }));
    if (multi) onChange?.([...selected, em]);
    else onChange?.(em);
    setQuery('');
    setOpen(multi);
    if (multi) inputRef.current?.focus();
  };
  const unpick = (em) => {
    if (multi) onChange?.(selected.filter((x) => x !== em));
    else onChange?.('');
    inputRef.current?.focus();
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => (options.length ? (i + 1) % options.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (options.length ? (i - 1 + options.length) % options.length : 0));
    } else if (e.key === 'Enter') {
      if (open && options[active]) {
        e.preventDefault();
        pick(options[active]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    } else if (e.key === 'Backspace' && !query && multi && selected.length) {
      unpick(selected[selected.length - 1]);
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  };

  const showInput = multi || !selected.length;
  const status = !typed
    ? (options.length ? '' : `Type at least ${MIN_CHARS} letters of a name or email`)
    : loading ? 'Searching…' : error ? error : options.length ? '' : `No workspace member matches “${query.trim()}”`;

  return (
    <div className="relative" data-testid={testId}>
      <div
        ref={anchorRef}
        className={`flex w-full flex-wrap items-center gap-1 border border-gray-300 bg-white px-2 text-sm focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 ${size === 'sm' ? 'min-h-[28px] rounded-md py-0' : 'min-h-[38px] rounded-lg py-1'} ${disabled ? 'bg-gray-100' : ''}`}
        onMouseDown={(e) => { if (e.target === e.currentTarget) { e.preventDefault(); inputRef.current?.focus(); } }}
      >
        {selected.map((em) => (
          <span key={em} data-testid={`${testId}-chip`}
            className="inline-flex max-w-full items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-800">
            <span className="truncate" title={em}>{names[em] ? `${names[em]} · ${em}` : em}</span>
            {!disabled ? (
              <button type="button" aria-label={`Remove ${em}`} onClick={() => unpick(em)}
                className="rounded-full px-1 text-gray-500 hover:bg-gray-200 hover:text-gray-800">×</button>
            ) : null}
          </span>
        ))}
        {showInput ? (
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-label={ariaLabel}
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={open && options[active] ? `${listId}-${active}` : undefined}
            autoComplete="off"
            disabled={disabled}
            value={query}
            placeholder={selected.length ? '' : placeholder}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            className={`min-w-[8rem] flex-1 border-0 bg-transparent px-1 text-sm outline-none focus:ring-0 disabled:cursor-not-allowed ${size === 'sm' ? 'py-0 leading-6' : 'py-1'}`}
          />
        ) : null}
      </div>
      {emptyHint && !selected.length ? <p className="mt-1 text-[11px] text-gray-500">{emptyHint}</p> : null}
      <PortalMenu anchorRef={anchorRef} open={open && !disabled && (options.length > 0 || !!status)}
        onClose={() => setOpen(false)} align="bottom-left" menuRole="presentation"
        width={anchorRef.current ? anchorRef.current.offsetWidth : undefined}>
        <ul id={listId} role="listbox" aria-label={ariaLabel} data-testid={`${testId}-list`}
          className="max-h-64 overflow-y-auto py-1" style={{ minWidth: anchorRef.current ? anchorRef.current.offsetWidth : 240 }}>
          {!typed && options.length ? (
            <li role="presentation" className="px-3 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
              Suggested
            </li>
          ) : null}
          {options.map((m, i) => (
            <li key={m.email} id={`${listId}-${i}`} role="option" aria-selected={i === active}
              data-testid={`${testId}-option`}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActive(i)}
              onClick={() => pick(m)}
              className={`cursor-pointer px-3 py-1.5 text-sm ${i === active ? 'bg-blue-50' : ''}`}>
              <div className="truncate text-gray-900"><Highlight text={m.displayName || m.email} prefix={query} /></div>
              {m.displayName ? (
                <div className="truncate text-[11px] text-gray-500"><Highlight text={m.email} prefix={query} /></div>
              ) : null}
            </li>
          ))}
          {status ? (
            <li role="presentation" data-testid={`${testId}-status`} className="px-3 py-2 text-xs text-gray-500">{status}</li>
          ) : null}
        </ul>
      </PortalMenu>
    </div>
  );
}
